import React from 'react';
import { BRAND_NAME, SPONSOR_NAME } from '../config/brand';
import confettiBg from '../assets/home/explainer-hero-confetti.jpg';
import step1Image from '../assets/access/landing-step-1-sube-contenido.jpg';
import step2Image from '../assets/access/landing-step-2-suma-views.jpg';
import step3Image from '../assets/access/landing-step-3-recibe-premios.jpg';

// Carrusel Explicativo (2026-07-17) — hero de la home, réplica del mock de la
// diseñadora: card redondeada con foto de papelitos de fondo + overlay oscuro,
// título "¿Como funciona?" (acento violeta) y los 3 pasos en cards translúcidas.
// Reusa las imágenes de pasos del landing de acceso (src/assets/access/).
//
// Es un "carrusel" porque a futuro va a rotar fondos/slides; POR AHORA tiene un
// solo fondo (el de papelitos), así que se renderiza estático el slide 0.

const PURPLE = '#948CFA'; // violeta de "funciona" (sampleado del mock)
const YELLOW = '#FFC53C'; // mismo amarillo del landing de acceso

const SLIDES = [{ background: confettiBg }];

interface Step {
  title: string;
  description: string;
  /** Línea extra destacada en amarillo (solo el paso 3). */
  highlight?: string;
  image: string;
}

const STEPS: Step[] = [
  {
    title: '1. Sube contenido',
    description: `Sube clips de ${SPONSOR_NAME} a TikTok o redes habilitadas.`,
    image: step1Image,
  },
  {
    title: '2. Suma views',
    description: `El conteo inicia tras registrarte en ${BRAND_NAME}.`,
    image: step2Image,
  },
  {
    title: '3. Recibe el pago.',
    description: '1 view = 1 punto.',
    highlight: 'Límite mensual: 500 USD. Reset fin de mes.',
    image: step3Image,
  },
];

export const ExplainerCarousel: React.FC = () => {
  const slide = SLIDES[0];

  return (
    <section
      aria-label='Cómo funciona'
      style={{
        position: 'relative',
        borderRadius: 24,
        overflow: 'hidden',
        marginBottom: 40,
        // Drop shadow del mock (Figma inspect): X0 Y20 blur40 spread0, negro 15%.
        boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
      }}
    >
      {/* capa foto + capa tinte oscuro, detrás del contenido */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          backgroundImage: `url(${slide.background})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          zIndex: 0,
        }}
      />
      {/* Overlay del mock (Figma inspect): linear gradient negro 80% → 40%.
          Va oscuro en la esquina del título (legibilidad) y se aclara hacia la
          derecha para dejar ver los papelitos. */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background: 'linear-gradient(115deg, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.4) 100%)',
          zIndex: 1,
        }}
      />

      <div style={{ position: 'relative', zIndex: 2, padding: 'clamp(28px, 4vw, 48px)' }}>
        <h2
          style={{
            margin: '0 0 14px',
            color: '#ffffff',
            fontWeight: 800,
            fontSize: 'clamp(30px, 4.5vw, 46px)',
            lineHeight: 1.1,
          }}
        >
          ¿Cómo <span style={{ color: PURPLE }}>funciona</span>?
        </h2>

        <p
          style={{
            margin: 0,
            color: 'rgba(255,255,255,0.86)',
            fontSize: 'clamp(14px, 1.6vw, 16px)',
            lineHeight: 1.55,
            maxWidth: 560,
          }}
        >
          Subí clips en tus redes, acumulá puntos según la interacción y canjealos por
          entradas, descuentos y productos oficiales.
        </p>

        <div
          style={{
            marginTop: 'clamp(24px, 3vw, 36px)',
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
            gap: 'clamp(14px, 2vw, 24px)',
          }}
        >
          {STEPS.map(step => (
            <article
              key={step.title}
              style={{
                borderRadius: 16,
                background: 'rgba(16, 10, 20, 0.55)',
                backdropFilter: 'blur(10px)',
                WebkitBackdropFilter: 'blur(10px)',
                border: '1px solid rgba(255,255,255,0.08)',
                padding: 'clamp(16px, 2vw, 22px)',
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
              }}
            >
              <h3
                style={{
                  margin: 0,
                  color: '#ffffff',
                  fontWeight: 800,
                  fontSize: 'clamp(19px, 2vw, 24px)',
                  lineHeight: 1.2,
                }}
              >
                {step.title}
              </h3>
              <p style={{ margin: 0, color: 'rgba(255,255,255,0.78)', fontSize: 14.5, lineHeight: 1.45 }}>
                {step.description}
              </p>
              {step.highlight && (
                <p style={{ margin: 0, color: YELLOW, fontWeight: 700, fontSize: 14.5, lineHeight: 1.45 }}>
                  {step.highlight}
                </p>
              )}
              <img
                src={step.image}
                alt={step.title}
                loading='lazy'
                style={{
                  marginTop: 'auto',
                  width: '100%',
                  aspectRatio: '512 / 286',
                  objectFit: 'cover',
                  borderRadius: 10,
                  display: 'block',
                }}
              />
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};
